import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { verifyOTPAndCompleteRegistration, sendOTP } from '../Actions/userActions';
import LoadingBox from '../Components/LoadingBox';
import MessageBox from '../Components/MessageBox';
import './OTPVerificationScreen.css';
import { useToast } from '../Context/ToastContext';

export default function OTPVerificationScreen() {
    const [otp, setOtp] = useState('');
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    
    const dispatch = useDispatch();
    const { success: showSuccess, error: showError } = useToast();
    const history = useHistory();
    const location = useLocation();
    
    // Get registration data from location state
    const registrationData = location.state;
    
    const otpVerification = useSelector((state) => state.otpVerification);
    const { loading, error, success } = otpVerification;

    // Timer effect for resend button
    useEffect(() => {
        if (timer > 0) {
            const countdown = setTimeout(() => setTimer(timer - 1), 1000);
            return () => clearTimeout(countdown);
        } else {
            setCanResend(true);
        }
    }, [timer]);

    // Redirect if no registration data
    useEffect(() => {
        if (!registrationData) {
            history.push('/register');
        }
    }, [registrationData, history]);

    // Redirect on successful verification
    useEffect(() => {
        if (success) {
            // Clear pending registration flag
            try { sessionStorage.removeItem('pendingRegistration'); } catch (_) {}
            const role = registrationData?.role || 'user';
            if (String(role).toLowerCase() === 'retailer') {
                history.replace('/pending-approval');
            } else {
                history.replace('/');
            }
        }
    }, [success, history, registrationData]);

    const handleVerifyOTP = (e) => {
        e.preventDefault();
        
        if (!otp.trim()) {
            showError('Please enter the OTP');
            return;
        }
        
        if (otp.length !== 6) {
            showError('Please enter a valid 6-digit OTP');
            return;
        }

        dispatch(verifyOTPAndCompleteRegistration(otp, registrationData));
    };

    const handleResendOTP = async () => {
        if (canResend && registrationData) {
            try {
                // Reset timer and send new OTP
                setTimer(120); // 2 minutes for resend
                setCanResend(false);
                
                // Send new OTP
                await dispatch(sendOTP(registrationData.mobile));
                showSuccess('New OTP sent to your mobile number');
            } catch (error) {
                console.error('Resend OTP error:', error);
                showError('Failed to resend OTP: ' + error.message);
                setCanResend(true); // Allow retry
            }
        }
    };

    if (!registrationData) {
        return <div>Redirecting...</div>;
    }

    return (
        <div className="otp-verification-screen">
            <div className="otp-container" role="dialog" aria-modal="true">
                <div className="otp-header">
                    <h1>Verify Your Mobile Number</h1>
                    <p>Verification code has been sent to +91{registrationData.mobile}</p>
                </div>

                <form className="otp-form" onSubmit={handleVerifyOTP}>
                    <div className="form-group">
                        {loading && <LoadingBox />}
                        {error && <MessageBox variant="danger">{error}</MessageBox>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="otp">Enter OTP</label>
                        <input
                            type="text"
                            id="otp"
                            placeholder="Enter 6-digit OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength="6"
                            className="otp-input"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <button 
                            type="submit" 
                            className="verify-btn"
                            disabled={loading || otp.length !== 6}
                        >
                            {loading ? 'Verifying...' : 'Verify OTP'}
                        </button>
                    </div>

                    <div className="form-group">
                        <button
                            type="button"
                            className={`resend-btn ${canResend ? 'active' : 'disabled'}`}
                            onClick={handleResendOTP}
                            disabled={!canResend}
                        >
                            {canResend ? 'Resend OTP' : `Resend in ${timer}s`}
                        </button>
                    </div>
                </form>

                <div className="otp-footer">
                    <p>Didn't receive the code? Check your SMS or try resending.</p>
                </div>
            </div>
        </div>
    );
}
